/**
 * Result monad.
 */
export interface Result<T, E> extends NonNullable<unknown> {
  /**
   * Return whether the Result is Ok.
   */
  isOk(): boolean

  /**
   * Return whether the Result is Err.
   */
  isErr(): boolean

  /**
   * Returns other if the result is Ok, otherwise returns the Err value of self.
   *
   * @param other
   */
  and<U>(other: Result<U, E>): Result<U, E>

  /**
   * Returns other if the result is Err, otherwise returns the Ok value of self.
   *
   * @param other
   */
  or<F>(other: Result<T, F>): Result<T, F>

  /**
   * Maps the result (if Ok).
   */
  map<O>(fn: (v: T) => O): Result<O, E>

  /**
   * Async variant of `map`.
   */
  mapAsync<O>(fn: (v: T) => Promise<O>): Promise<Result<O, E>>

  /**
   * Maps the error (if Err).
   */
  mapErr<NE>(fn: (v: E) => NE): Result<T, NE>

  /**
   * Async variant of `mapErr`.
   */
  mapErrAsync<NE>(fn: (v: E) => Promise<NE>): Promise<Result<T, NE>>

  /**
   * If Ok, calls the function and the result of that is returned.
   * If Err, the Err result is returned.
   */
  andThen<U>(fn: (v: T) => Result<U, E>): Result<U, E>

  /**
   * Async variant of `andThen`.
   */
  andThenAsync<U>(fn: (v: T) => Promise<Result<U, E>>): Promise<Result<U, E>>

  /**
   * If Ok, returns the Ok result.
   * If Err, calls the function and the result of that is returned.
   */
  orElse<F>(fn: (e: E) => Result<T, F>): Result<T, F>

  /**
   * Async variant of `orElse`.
   */
  orElseAsync<F>(fn: (e: E) => Promise<Result<T, F>>): Promise<Result<T, F>>

  /**
   * Match on the result.
   *
   * @param matcher
   */
  match<O>(matcher: ResultMatcher<T, E, O>): O

  /**
   * Async variant of `match`.
   *
   * @param matcher
   */
  matchAsync<O>(matcher: AsyncResultMatcher<T, E, O>): Promise<O>

  /**
   * Unwraps the result, uses the error factory to create an error if
   * it is an `Err`.
   *
   * @param errorFactory
   */
  unwrap(errorFactory?: (e: E) => Error): T | never

  /**
   * Unwraps the result, uses the error factory to create an error if
   * it is an `Ok`.
   *
   * @param errorFactory
   */
  unwrapErr(errorFactory?: (v: T) => Error): E | never

  /**
   * Unwraps the result, uses the error factory to create an error if
   * it is an `Ok`.
   *
   * @param errorFactory
   */
  unwrapOr(fallbackValue: T): T | never

  /**
   * Returns either the value or the error.
   * This is a TypeScript thing and isn't present in your usual `Result` monad implementation.
   */
  splice(): T | E

  /**
   * Allows for side effects without altering the Result.
   */
  tap(fn: (v: T) => void): Result<T, E>

  /**
   * Async variant of `tap`.
   */
  tapAsync(fn: (v: T) => Promise<void>): Promise<Result<T, E>>

  /**
   * Allows for side effects without altering the Result.
   */
  tapErr(fn: (e: E) => void): Result<T, E>

  /**
   * Returns a string representation of an object.
   */
  toString(): string
}

/**
 * Result matcher interface.
 */
export interface ResultMatcher<V, E, O> {
  Ok: (value: V) => O
  Err: (err: E) => O
}

/**
 * Async variant of `ResultMatcher`
 */
export interface AsyncResultMatcher<V, E, O> {
  Ok: (value: V) => Promise<O>
  Err: (err: E) => Promise<O>
}

/**
 * Creates an Ok result.
 */
export const Ok = <R>(value: R) =>
  createResult<R, never>(true, value, null as never)

/**
 * Creates an Err result.
 */
export const Err = <E, R>(error: E) =>
  createResult<R, E>(false, null as any, error)

// Combinators as higher order functions.
export const Result = {
  ...createHigherOrderCombinators(),
  Ok,
  Err,
  /**
   * Converts a nilable (nullable or undefinedable (lol)) to a a Result where the `Ok` case is non-nil and
   * the `Err` case is `null`.
   */
  fromNilable<T>(
    filthy: T | undefined | null,
  ): Result<Exclude<T, null | undefined>, null> {
    return filthy === null || filthy === undefined
      ? Err(null)
      : Ok(filthy as Exclude<T, null | undefined>)
  },
}

/**
 * Result creator.
 *
 * @param isOk
 * @param value
 * @param error
 */
function createResult<T, E>(isOk: boolean, value: T, error: E): Result<T, E> {
  const match = <O>(matcher: ResultMatcher<T, E, O>) =>
    isOk ? matcher.Ok(value) : matcher.Err(error)

  const matchAsync = async <O>(matcher: AsyncResultMatcher<T, E, O>) =>
    isOk ? matcher.Ok(value) : matcher.Err(error)

  const self: Result<T, E> = {
    match,
    matchAsync,
    isOk: () => isOk,
    isErr: () => !isOk,
    and: (other) => (isOk ? other : Err(error)),
    or: (other) => (isOk ? Ok(value) : other),
    map: (fn) => (isOk ? Ok(fn(value)) : Err(error)),
    mapAsync: async (fn) => (isOk ? Ok(await fn(value)) : Err(error)),
    mapErr: (fn) => (isOk ? Ok(value) : Err(fn(error))),
    mapErrAsync: async (fn) => (isOk ? Ok(value) : Err(await fn(error))),
    andThen: (fn: any) => (isOk ? fn(value) : Err(error)),
    andThenAsync: async (fn: any) => (isOk ? fn(value) : Err(error)),
    orElse: (fn: any) => (isOk ? Ok(value) : fn(error)),
    orElseAsync: async (fn: any) => (isOk ? Ok(value) : fn(error)),
    toString: () => (isOk ? `Ok { ${value} }` : `Err { ${error} }`),
    splice: () => (isOk ? value : error),
    unwrap: (errorFactory?: (e: E) => Error) => {
      errorFactory = errorFactory || defaultErrorFactory
      if (isOk) {
        return value
      }

      throw errorFactory(error)
    },
    unwrapErr: (errorFactory?: (v: T) => Error) => {
      errorFactory = errorFactory || defaultErrorFactory
      if (!isOk) {
        return error
      }

      throw errorFactory(value)
    },
    unwrapOr: (fallbackValue: T): T | never =>
      match<T>({ Ok: identity, Err: () => fallbackValue }),
    tap: (fn): Result<T, E> => {
      if (isOk) {
        fn(value)
        return Ok(value)
      }
      return Err(error)
    },
    tapAsync: async (fn): Promise<Result<T, E>> => {
      if (isOk) {
        await fn(value)
        return Ok(value)
      }
      return Err(error)
    },
    tapErr: (fn): Result<T, E> => {
      if (!isOk) {
        fn(error)
        return Err(error)
      }
      return Ok(value)
    },
  }

  return self
}

/**
 * Identity function (returns the value passed to it).
 */
function identity<T>(v: T): T {
  return v
}

/**
 * Default error factory for `unwrap`.
 * @param e
 */
function defaultErrorFactory(e: unknown) {
  if (e instanceof Error) {
    return e
  }
  return new ReferenceError('Unexpected result when unwrapping')
}

/**
 * Combinators as higher order functions.
 *
 * For example,
 *
 *  `Result.map(v => v + 2)`
 *
 * would be the same as
 *
 *  `(res) => res.map(v => v + 2)`
 *
 * This is done for all the combinators on the `Result` type.
 */
type HigherOrderCombinators = {
  and<T, E, U>(other: Result<U, E>): (result: Result<T, E>) => Result<U, E>
  or<T, E, F>(other: Result<T, F>): (result: Result<T, E>) => Result<T, F>
  map<U, T, E>(fn: (v: T) => U): (result: Result<T, E>) => Result<U, E>
  mapAsync<U, T, E>(
    fn: (v: T) => Promise<U>,
  ): (result: Result<T, E>) => Promise<Result<U, E>>
  mapErr<T, E, F>(fn: (e: E) => F): (result: Result<T, E>) => Result<T, F>
  mapErrAsync<T, E, F>(
    fn: (e: E) => Promise<F>,
  ): (result: Result<T, E>) => Promise<Result<T, F>>
  andThen<T, E, U>(
    fn: (v: T) => Result<U, E>,
  ): (result: Result<T, E>) => Result<U, E>
  andThenAsync<T, E, U>(
    fn: (v: T) => Promise<Result<U, E>>,
  ): (result: Result<T, E>) => Promise<Result<U, E>>
  orElse<T, E, F>(
    fn: (e: E) => Result<T, F>,
  ): (result: Result<T, E>) => Result<T, F>
  orElseAsync<T, E, F>(
    fn: (e: E) => Promise<Result<T, F>>,
  ): (result: Result<T, E>) => Promise<Result<T, F>>
  match<T, E, O>(matcher: ResultMatcher<T, E, O>): (result: Result<T, E>) => O
  match<T, E, O>(ok: (v: T) => O, err: (e: E) => O): (result: Result<T, E>) => O
  unwrap<T, E>(
    errorFactory?: (e: E) => Error,
  ): (result: Result<T, E>) => T | never
  unwrapErr<T, E>(
    errorFactory?: (v: T) => Error,
  ): (result: Result<T, E>) => E | never
  splice<T, E>(): (result: Result<T, E>) => T | E
  tap<T, E>(fn: (v: T) => void): (result: Result<T, E>) => Result<T, E>
  tapAsync<T, E>(
    fn: (v: T) => Promise<void>,
  ): (result: Result<T, E>) => Promise<Result<T, E>>
  tapErr<T, E>(fn: (e: E) => void): (result: Result<T, E>) => Result<T, E>
}

/**
 * Creates the higher-order variants of the result combinators.
 */
function createHigherOrderCombinators(): HigherOrderCombinators {
  const make =
    (key: keyof Result<unknown, unknown>) =>
    (...args: any[]) =>
    (result: any) =>
      result[key](...args)
  return {
    and: make('and'),
    or: make('or'),
    map: make('map'),
    mapAsync: make('mapAsync'),
    mapErrAsync: make('mapErrAsync'),
    mapErr: make('mapErr'),
    andThen: make('andThen'),
    andThenAsync: make('andThenAsync'),
    match: make('match'),
    orElse: make('orElse'),
    orElseAsync: make('orElseAsync'),
    unwrap: make('unwrap'),
    unwrapErr: make('unwrapErr'),
    splice: make('splice'),
    tap: make('tap'),
    tapAsync: make('tapAsync'),
    tapErr: make('tapErr'),
  }
}
